import { useState, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Package, Tag, Star, Calendar, MessageCircle, ArrowLeftRight, Trash2, MessageSquare, BadgeCheck, Trophy, Zap } from 'lucide-react';
import { useProfile, useUserListings, useDeleteListing, useUserReviews, useSubmitReview, useUploadAvatar, useForumUserPosts, useUserLevel, useUserTrophies, useUserReputation } from '@/hooks/useApi';
import PeekIntoTheVault from '@/components/PeekIntoTheVault';
import { useAuthStore } from '@/store';
import SEO from '@/components/SEO';
import { Listing, Review } from '@/types';
import CardImage from '@/components/CardImage';
import { formatPrice, formatDate } from '@/utils';
import UserAvatar from '@/components/UserAvatar';
import LevelProgress from '@/components/progression/LevelProgress';
import ReputationBadge from '@/components/progression/ReputationBadge';
import TrophyCard from '@/components/progression/TrophyCard';
import ReportButton from '@/components/ReportButton';
import BlockUserButton from '@/components/BlockUserButton';
import AvatarCropModal from '@/components/AvatarCropModal';

function ForumActivitySection({ userId }: { userId: string }) {
  const { data: posts = [] } = useForumUserPosts(userId);
  if (posts.length === 0) {
    return (
      <section className="mt-8">
        <h2 className="text-xl font-bold mb-4">Forum Activity</h2>
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-gray-200 dark:border-zinc-800 p-6 text-center text-sm text-gray-500 dark:text-zinc-500">
          No forum posts yet.
        </div>
      </section>
    );
  }
  return (
    <section className="mt-8">
      <h2 className="text-xl font-bold mb-4">Forum Activity</h2>
      <div className="space-y-2">
        {posts.slice(0, 5).map((post: any) => (
          <Link key={post.id} to={`/forum/threads/${post.thread_id}`}
            className="flex items-start gap-3 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors">
            <MessageSquare className="h-4 w-4 text-gray-500 dark:text-zinc-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-gray-800 dark:text-zinc-200 truncate">{post.thread_title}</p>
              <p className="text-xs text-gray-500 dark:text-zinc-500 truncate mt-0.5">{post.body?.slice(0, 100)}</p>
            </div>
            <span className="text-xs text-gray-500 dark:text-zinc-500 flex-shrink-0">{formatDate(post.created_at, 'MMM d')}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ProfilePage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const { data: profile, isLoading } = useProfile(id);
  const { data: listings, isLoading: listingsLoading } = useUserListings(id);
  const { mutate: deleteListing } = useDeleteListing();
  const { data: reviews = [] } = useUserReviews(id ?? '');
  const { mutate: submitReview, isLoading: submitting } = useSubmitReview(id ?? '');
  const { mutate: uploadAvatar } = useUploadAvatar();

  // Progression
  const profileUserId = id ?? profile?.id ?? currentUser?.id;
  const { data: userLevel }      = useUserLevel(profileUserId);
  const { data: userTrophies = [] } = useUserTrophies(profileUserId);
  const { data: userReputation } = useUserReputation(profileUserId);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('avatar.jpg');

  const handleAvatarClick = () => avatarInputRef.current?.click();
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropFileName(file.name);
      setCropSrc(URL.createObjectURL(file));
    }
    e.target.value = '';
  };
  const handleCropConfirm = useCallback((file: File) => {
    uploadAvatar(file);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [uploadAvatar, cropSrc]);
  const handleCropCancel = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  if (isLoading) {
    return (
      <div className="container-custom py-12">
        <div className="skeleton h-40 rounded-2xl mb-6" />
        <div className="grid md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container-custom py-24 text-center">
        <p className="text-5xl mb-4">👤</p>
        <h2 className="text-2xl font-bold mb-2">User not found</h2>
      </div>
    );
  }

  const isOwnProfile = !id;
  const averageReviewRating =
    reviews.length > 0
      ? reviews.reduce((sum: number, review: Review) => sum + review.rating, 0) / reviews.length
      : null;
  const displayRating =
    typeof profile.rating === 'number'
      ? profile.rating
      : typeof profile.seller_rating === 'number' && profile.seller_rating > 0
        ? profile.seller_rating
        : averageReviewRating;
  const activeListings = Array.isArray(listings)
    ? listings
        .filter((listing: Listing) => listing.status === 'active')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5)
    : [];

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {cropSrc && (
        <AvatarCropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
      <SEO
        title={`${(profile as any)?.username ?? 'Collector'}'s Profile`}
        description={`View ${(profile as any)?.username ?? 'this collector'}'s trading card listings, reviews, and collection on Open Card Vault.`}
        url={`/profile/${id}`}
        noIndex={false}
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white">
        <div className="container-custom py-10">
          <div className="flex items-center gap-6">
            <div className="ring-4 ring-white/30 rounded-full overflow-visible">
              <UserAvatar
                username={profile.username}
                avatarUrl={profile.avatar_url}
                size="xl"
                editable={isOwnProfile}
                onClick={handleAvatarClick}
                level={userLevel?.level}
                trophiesHref={isOwnProfile ? '/trophies' : `/trophies/${profile.id}`}
              />
              {isOwnProfile && (
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              )}
            </div>

            <div className="flex-1">
              <h1 className="text-3xl font-display font-bold flex items-center gap-2 flex-wrap">
                {profile.username}
                {profile.email_verified && (
                  <BadgeCheck className="h-6 w-6 text-green-400" title="Verified email" />
                )}
                {userLevel && (
                  <span className="inline-flex items-center gap-1 bg-white dark:bg-zinc-900/20 text-white text-sm font-semibold px-2.5 py-1 rounded-full">
                    <Zap className="h-3.5 w-3.5" />
                    Lv. {userLevel.level}
                  </span>
                )}
                {userReputation && userReputation.trust_level !== 'New' && (
                  <span className="inline-flex items-center gap-1 bg-white dark:bg-zinc-900/20 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    {userReputation.trust_level}
                  </span>
                )}
              </h1>
              <p className="text-brand-100 flex items-center gap-1 mt-1">
                <Calendar className="h-4 w-4" />
                Joined {formatDate(profile.created_at, 'MMM d, yyyy')}
              </p>
              {profile.bio && <p className="text-brand-100 mt-2 max-w-xl">{profile.bio}</p>}
            </div>

            <div className="flex gap-3">
              {isOwnProfile ? (
                <Link to="/settings" className="btn-outline border-white text-white hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/10 py-2">
                  Edit Profile
                </Link>
              ) : (
                <button
                  onClick={async () => {
                    if (!currentUser) {
                      navigate('/login');
                      return;
                    }
                    try {
                      const { apiClient } = await import('@/lib/api-client');
                      const response = await apiClient.post<any>('/conversations', {
                        recipient_id: profile.id,
                      });
                      const convId = response.data?.conversation?.id;
                      if (convId) navigate(`/messages/${convId}`);
                    } catch (e) {
                      alert('Error: ' + String(e));
                    }
                  }}
                  className="btn-outline border-white text-white hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/10 py-2 flex items-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" /> Message
                </button>
              )}
            </div>
            {!isOwnProfile && profile && (
              <div className="flex gap-4 mt-2">
                <ReportButton
                  entityType="profile"
                  entityId={profile.id}
                  reportedUserId={profile.id}
                  className="text-white/70 hover:text-white"
                />
                <BlockUserButton
                  userId={profile.id}
                  username={profile.username}
                  className="text-white/70 hover:text-white"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 text-center">
            <Package className="h-6 w-6 mx-auto mb-1 text-brand-500" />
            <p className="text-2xl font-bold">{profile.collection_count ?? 0}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">Cards</p>
          </div>
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 text-center">
            <Tag className="h-6 w-6 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold">{profile.total_sales ?? 0}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">Sales</p>
          </div>
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 text-center">
            <ArrowLeftRight className="h-6 w-6 mx-auto mb-1 text-purple-500" />
            <p className="text-2xl font-bold">{profile.total_trades ?? 0}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">Trades</p>
          </div>
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 text-center">
            <Star className="h-6 w-6 mx-auto mb-1 text-yellow-500 fill-yellow-500" />
            <p className="text-2xl font-bold">{displayRating !== null ? displayRating.toFixed(1) : '—'}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">Rating</p>
          </div>
        </div>

        {/* ── Progression: Level + Reputation + Trophy showcase ── */}
        {(userLevel || userReputation || userTrophies.length > 0) && (
          <section className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {userLevel     && <LevelProgress level={userLevel} />}
              {userReputation && <ReputationBadge reputation={userReputation} />}
            </div>

            {userTrophies.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    <h2 className="font-bold text-gray-900 dark:text-zinc-100">Trophies</h2>
                    <span className="text-sm text-gray-500 dark:text-zinc-500">{userTrophies.length} earned</span>
                  </div>
                  <Link
                    to={isOwnProfile ? '/trophies' : `/trophies/${profileUserId}`}
                    className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-medium"
                  >
                    View all →
                  </Link>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {userTrophies
                    .filter(t => t.is_displayed)
                    .slice(0, 12)
                    .map(ut => (
                      <TrophyCard
                        key={ut.id}
                        trophy={{ ...ut.trophy, earned: true, progress: ut.progress, earned_at: ut.earned_at }}
                        size="sm"
                      />
                    ))}
                </div>

                {userTrophies.filter(t => t.is_displayed).length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-zinc-500 text-center py-3">No trophies displayed yet.</p>
                )}
              </div>
            )}
          </section>
        )}

        <PeekIntoTheVault userId={profile.id} username={isOwnProfile ? undefined : profile.username} />

        <section className="mb-10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Active Listings</h2>
            <div className="flex items-center gap-4">
              {Array.isArray(listings) && listings.length > 0 && (
                <Link
                  to={isOwnProfile ? '/profile/listings' : `/profile/${profile.id}/listings`}
                  className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline"
                >
                  View all
                </Link>
              )}
              {isOwnProfile && (
                <Link to="/listings/create" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline">
                  + New Listing
                </Link>
              )}
            </div>
          </div>

          {listingsLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
            </div>
          ) : activeListings.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-200 dark:border-zinc-800 p-10 text-center">
              <p className="text-gray-500 dark:text-zinc-500 text-sm">No active listings.</p>
              {isOwnProfile && (
                <Link to="/listings/create" className="text-brand-600 dark:text-brand-400 font-medium text-sm hover:underline inline-block mt-2">
                  Create your first listing →
                </Link>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
              <div className="space-y-3">
                {activeListings.map((listing: Listing) => (
                  <div key={listing.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors group">
                    <Link to={`/listings/${listing.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="h-12 w-9 rounded-lg overflow-hidden flex-shrink-0">
                        <CardImage src={listing.image_urls?.[0]} alt={listing.card?.name} cardName={listing.card?.name} game={listing.card?.game} className="w-full h-full" compact />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{listing.card?.name}</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-500">{listing.condition}</p>
                      </div>
                      {listing.listing_type === 'auction' ? (
                        <span className="text-sm font-bold text-orange-600">
                          {listing.current_bid ? `Top: ${formatPrice(listing.current_bid)}` : `Start: ${formatPrice(listing.starting_price ?? listing.price)}`}
                        </span>
                      ) : (listing.listing_type as string) === 'trade' ? (
                        <span className="text-xs font-semibold text-purple-600">Trade</span>
                      ) : (
                        <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{formatPrice(listing.price)}</span>
                      )}
                    </Link>
                    {isOwnProfile && (
                      <button
                        onClick={() => { if (window.confirm('Delete this listing?')) deleteListing(listing.id); }}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-1 transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <ForumActivitySection userId={profile.id} />

        <section>
          <h2 className="text-xl font-bold mb-4">
            Reviews {reviews.length > 0 && <span className="text-gray-500 dark:text-zinc-500 font-normal text-base">({reviews.length})</span>}
          </h2>

          {!isOwnProfile && currentUser && !reviewSubmitted && (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5 mb-5">
              <h3 className="font-semibold text-sm text-gray-700 dark:text-zinc-300 mb-3">Leave a Review</h3>

              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    onMouseEnter={() => setReviewHover(star)}
                    onMouseLeave={() => setReviewHover(0)}
                    className="focus:outline-none"
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                  >
                    <Star
                      className={`h-7 w-7 transition-colors ${
                        star <= (reviewHover || reviewRating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-200'
                      }`}
                    />
                  </button>
                ))}
                {reviewRating > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-zinc-500 self-center">
                    {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][reviewRating]}
                  </span>
                )}
              </div>

              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your experience (optional)"
                rows={3}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
              />

              <button
                disabled={reviewRating === 0 || submitting}
                onClick={() => {
                  submitReview(
                    { rating: reviewRating, comment: reviewComment || undefined },
                    { onSuccess: () => setReviewSubmitted(true) },
                  );
                }}
                className="mt-3 btn-primary py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </div>
          )}

          {!isOwnProfile && currentUser && reviewSubmitted && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl px-5 py-3 text-sm text-green-700 dark:text-green-300 font-medium mb-5">
              Your review has been submitted. Thank you!
            </div>
          )}

          {reviews.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-gray-200 dark:border-zinc-800 p-8 text-center text-gray-500 dark:text-zinc-500 text-sm">
              No reviews yet.
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map((review: Review) => (
                <div key={review.id} className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <UserAvatar
                      username={review.reviewer_username ?? '?'}
                      size="sm"
                    />
                    <div>
                      <p className="font-semibold text-sm">{review.reviewer_username}</p>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3.5 w-3.5 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="ml-auto text-xs text-gray-500 dark:text-zinc-500">{formatDate(review.created_at, 'MMM d, yyyy')}</span>
                  </div>
                  {(review.comment ?? review.review_text) && (
                    <p className="text-sm text-gray-600 dark:text-zinc-400">{review.comment ?? review.review_text}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
